/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import 'reflect-metadata';
import ValidationResponse from './ValidationResponse.js';
import { EmptyValue } from '../error/EmptyValue.js';
import RequestMapper from '../mapping/RequestMapper.js';
import { getOptionalFields } from '../../../../core/decorator/OptionalDecorator.js';
import { RuntimeError } from '../../../../core/error/RuntimeError.js';
export default class ValidatedRequest {
    constructor(schema) {
        this.schema = schema;
    }
    validate(key) {
        const vals = [];
        if (!key) {
            const filteredEntries = this.filterSchemaFromProps();
            filteredEntries.forEach((key) => {
                this.pushValidations(key, vals);
            });
        }
        else {
            this.pushValidations(key, vals);
        }
        return vals;
    }
    getOptionalFields() {
        let keys = [];
        keys = Object.keys(getOptionalFields(this) ?? {});
        return keys;
    }
    isOptional(key) {
        return this.getOptionalFields().includes(key);
    }
    getProperty(propertyName) {
        if (this[propertyName] === undefined) {
            const privateKeys = {
                dash: `_${String(propertyName)}`,
                hash: `#${String(propertyName)}`,
            };
            if (this[privateKeys.dash]) {
                return this[privateKeys.dash];
            }
            else {
                return this[privateKeys.hash];
            }
        }
        return this[propertyName];
    }
    runValidation(propertyName, val) {
        if (this?.schema[propertyName] && val !== undefined) {
            try {
                const err = this.schema[propertyName]?.(val);
                if (err?.length && err.length > 0) {
                    return new ValidationResponse(propertyName.toString(), err);
                }
            }
            catch (err) {
                return new ValidationResponse(propertyName.toString(), [
                    err,
                ]);
            }
        }
        else if (this?.schema[propertyName] &&
            !this.isOptional(propertyName) &&
            val === undefined) {
            return new ValidationResponse(propertyName.toString(), [
                new EmptyValue(propertyName),
            ]);
        }
        else if (!this?.schema[propertyName] &&
            !this.isOptional(propertyName)) {
            throw new RuntimeError(`Invalid validation schema for property '${propertyName.toString()}'. Did you forget to add the validation?`);
        }
    }
    filterSchemaFromProps() {
        const schemaEntries = Object.keys(this.schema);
        const entries = RequestMapper.renamePrivateProps(Object.keys(this));
        const filteredEntries = schemaEntries.filter((value) => entries.includes(value));
        return filteredEntries;
    }
    pushValidations(key, vals) {
        try {
            const err = this.runValidation(key, this.getProperty(key));
            err && vals.push(err);
        }
        catch (err) {
            vals.push(new ValidationResponse(key.toString(), [err]));
        }
    }
}
